/* Feb-2026 · "Bonus tools available" banner.
 *
 * Polls `/revenue-features/mine` on mount. When newly-released
 * features exist since the user's last acknowledgement, we surface
 * a dismissable banner listing them. Click Dismiss → POST /acknowledge.
 */
import React, { useEffect, useState } from 'react';
import { paymentsApi } from '@/lib/api';
import { X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

// We use `paymentsApi.http` internally — simpler to just use fetch here
// because the endpoint is unauthenticated-safe (returns 401 gracefully).
const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

async function fetchMineOnce() {
  try {
    const r = await fetch(`${API}/revenue-features/mine`, { credentials: 'include' });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}
async function ackOnce() {
  try {
    await fetch(`${API}/revenue-features/acknowledge`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
  } catch { /* best-effort */ }
}

export default function BonusToolsBanner() {
  const [rows, setRows] = useState([]);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetchMineOnce().then((data) => {
      if (!mounted) return;
      setRows((data?.new || []));
    });
    return () => { mounted = false; };
  }, []);

  if (hidden || !rows || rows.length === 0) return null;

  const dismiss = async () => {
    setHidden(true);
    await ackOnce();
  };

  return (
    <div
      className="my-3 rounded-md border border-emerald-500/40 bg-emerald-500/5 px-3 py-2.5 text-[12px] shadow-sm"
      role="status"
      data-testid="bonus-tools-banner"
    >
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-4 w-4 text-emerald-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-semibold text-emerald-500">
            {rows.length === 1 ? 'A new bonus tool is available' : `${rows.length} new bonus tools are available`}
          </div>
          <ul className="mt-1 space-y-0.5">
            {rows.slice(0, 5).map((r) => (
              <li key={r.key} className="text-[11.5px] text-foreground" data-testid={`bonus-tools-item-${r.key}`}>
                <b>{r.title}</b>
                <span className="ml-1 text-muted-foreground">— {r.description}</span>
              </li>
            ))}
          </ul>
          {rows.length > 5 && (
            <div className="mt-1 text-[10.5px] text-muted-foreground">
              …plus {rows.length - 5} more. Head to Settings → Bonus Tools to see everything.
            </div>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={dismiss}
          className="h-6 px-2 text-[10.5px]"
          data-testid="bonus-tools-dismiss"
        >
          <X className="h-3 w-3" /> Got it
        </Button>
      </div>
    </div>
  );
}
